import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { fetchMisTableOptions } from '../../../../../../store/company/action';
import { useAppSelector } from '../../../../../../app/hooks';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { isLenderType } from '../../../../../../utils/utils';
import { useElementWidth } from '../../../../../../utils/customHooks';
import { updateQueryParams } from '../../../../../../utils/searchParamUtils';
import { DATA_ROOM_URL_PARAMS, PROVISIONAL_FINANCIALS_DOC_TYPE_ID } from '../../../../../DataRoom/constants';

import { appendUnmatchedRows, BALANCE_SHEET_LAYOUT, CASH_FLOW_LAYOUT, PROFIT_AND_LOSS_LAYOUT } from './provisionalStatementLayout';
import { ProvisionalStatementKey, resolveStatementMetrics } from './provisionalStatementTypes';
import { useProvisionalStatementData } from './useProvisionalStatementData';
import {
    AuditedRowSpec,
    PROVISIONAL_UNIT_STORAGE_KEY,
    CASH_FLOW_INDIRECT_MIS,
    CASH_FLOW_INDIRECT_LAYOUT,
    keepComputablePeriods,
} from '../../../AuditedFinancialsVisualisation/auditedStatementLayout';

import { useUnitPreference } from '../../../AuditedFinancialsVisualisation/useUnitPreference';
import { useAuditedType, toNature } from '../../../AuditedFinancialsVisualisation/useAuditedType';
import { FinancialNature, natureToAuditedType } from '../../../AuditedFinancialsVisualisation/auditedTypeUtils';
import { useFinancialsIngestionStatus } from '../../financialsIngestion/useFinancialsIngestionStatus';
import { FINANCIALS_FILE_TYPE } from '../../financialsIngestion/constants';
import { useDocIdFileViewer } from '../../../../../../common/DocumentViewer/useDocIdFileViewer';
import { useIsMobile } from '../../../../../../common/_custom/Hooks/useIsMobile';
import { PROVISIONAL_FILE_NATURE_NOTE, PROVISIONAL_FILE_VIEW_MIS, PROVISIONAL_FILE_VIEW_STATUS } from './fileView/constants';
import { useProvisionalFiles } from './fileView/useProvisionalFiles';
import { fetchProvisionalNotIngestedCount } from './fileView/apiHelper';
import { useProvisionalFileStatementData } from './fileView/useProvisionalFileStatementData';
import { useProvisionalFileDelete } from './fileView/useProvisionalFileDelete';

// Collapse thresholds: measured live (Gilroy, ControlsLeft content box) + 8px buffer, rounded up to the next 5; re-measure if the row changes.
export const HEADER_COLLAPSE_ACTIONS_WIDTH_INVESTOR = 1070; // 1058 measured with the removed Upload Data pill — conservative until re-measured
export const HEADER_COLLAPSE_ACTIONS_WIDTH_LENDER = 830; // 819 measured
export const HEADER_HIDE_FILE_LABEL_WIDTH = 575; // 565 measured
export const HEADER_HIDE_TITLE_WIDTH = 540; // 530 measured

const filterParentHeader = (data: any) => ({
    ...data,
    headers: data?.headers?.filter((h: any) => h?.name !== 'Parent') ?? [],
});

export interface ProvisionalStatutorySection {
    key: ProvisionalStatementKey;
    layout: AuditedRowSpec[];
    data: any;
}

interface UseProvisionalFinancialsLogicParams {
    /** RECUR-28096 — suppresses the financials ingestion poll in PDF-report mode. */
    isPDFReport?: boolean;
}

/**
 * State, effects and derived data for the stacked Provisional Financials page (RECUR-27640),
 * colocated per CLAUDE.md so the component file stays composition + JSX.
 *
 * The five `useProvisionalStatementData` instances live here so they still run once per page, not
 * once per rendered section.
 */
export function useProvisionalFinancialsLogic({ isPDFReport = false }: UseProvisionalFinancialsLogicParams = {}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryParams = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const orgId = queryParams.id ?? '';
    const isLender = isLenderType();
    const isMobile = useIsMobile();
    const user = useAppSelector((state) => state.user.userData);
    const isPartner = user?.accountType === USER_TYPE.PARTNER;

    // Options are held WITH the org they belong to, and everything visible is derived from whether
    // that org is still the current one. An effect runs after commit, so clearing state inside one
    // always paints the previous org's options (and therefore its figures) for a frame first — not
    // acceptable on a financials surface. Deriving removes that window entirely.
    const [heldOptions, setHeldOptions] = useState<{
        orgId: string;
        metricOptions: string[];
        rawOptions: any[];
        dataFormatOptions: string[];
    }>({ orgId: '', metricOptions: [], rawOptions: [], dataFormatOptions: [] });
    const optionsRequestRef = useRef(0);

    const [unit, setUnit] = useUnitPreference(PROVISIONAL_UNIT_STORAGE_KEY);

    // Opens a period header's source file(s) in the shared FileViewerDrawer. Kept here rather
    // than inside SourceFileTag's own logic hook, since the drawer renders once at the
    // page level, not once per chip.
    const viewer = useDocIdFileViewer();

    const optionsAreCurrent = heldOptions.orgId === orgId;
    const metricOptions = optionsAreCurrent ? heldOptions.metricOptions : [];
    const rawOptions = optionsAreCurrent ? heldOptions.rawOptions : [];
    const dataFormatOptions = optionsAreCurrent ? heldOptions.dataFormatOptions : [];
    // With no org there is nothing to wait for; otherwise we are loading until this org's own
    // options are the ones being held.
    const optionsLoading = orgId ? !optionsAreCurrent : false;

    // The shared Standalone/Consolidated selection; its availability here is the provisional FY pair.
    // Statement requests carry the nature in their key, so a change refetches without a callback.
    const { auditedType, selectedNature, auditedTypeOptionsObject, handleConsolidationChange, setAuditedTypeFromYears } = useAuditedType(
        undefined,
        orgId,
    );
    const requestNature = toNature(auditedType) ?? selectedNature;

    // Extracted to a callable (mirrors AuditedFinancialsVisualisation.tsx's fetchOptions) so
    // RECUR-28096's ingestion-completion handler can re-fetch options on demand, not just on
    // mount/org-change. The optionsRequestRef stale-guard is unchanged — it is what stops a
    // previous org's options landing after a switch.
    const fetchOptions = useCallback(() => {
        const requestId = ++optionsRequestRef.current;
        const isStale = () => requestId !== optionsRequestRef.current;
        dispatch(
            fetchMisTableOptions({
                orgId,
                source: 'AicaFinancials',
                type: 'mappedData',
                onSuccess: (res) => {
                    if (isStale()) return;
                    const blocks = res?.data?.data ?? [];
                    // First, so the pair is in place in the same render that sets the metrics.
                    const consolidationBlock = blocks.find((block: any) => block?.latestProvisionalStandaloneFY !== undefined);
                    setAuditedTypeFromYears(
                        consolidationBlock?.latestConsolidatedFY ?? null,
                        consolidationBlock?.latestStandaloneFY ?? null,
                        {
                            consolidatedFY: consolidationBlock?.latestProvisionalConsolidatedFY ?? null,
                            standaloneFY: consolidationBlock?.latestProvisionalStandaloneFY ?? null,
                        },
                    );
                    setHeldOptions({
                        orgId,
                        metricOptions: (blocks[0]?.misOptions ?? []).map((item: any) => item?.misDisplayName),
                        rawOptions: blocks[0]?.misOptions ?? [],
                        dataFormatOptions: (blocks[1]?.dataFormats ?? []).map((item: any) => item?.dataFormat),
                    });
                },
                // The saga calls this on a non-200 AND (since RECUR-27640) on a thrown request.
                // Hold empty options for this org so the page settles into its empty state rather
                // than pinning on the spinner with no error path.
                onError: () => {
                    if (isStale()) return;
                    setHeldOptions({ orgId, metricOptions: [], rawOptions: [], dataFormatOptions: [] });
                },
            }),
        );
    }, [dispatch, orgId, setAuditedTypeFromYears]);

    useEffect(() => {
        if (orgId) fetchOptions();
    }, [orgId, fetchOptions]);

    const resolvedMetrics = useMemo(() => resolveStatementMetrics(metricOptions), [metricOptions]);

    // Derived against the current org and list, so an org switch or a vanished file falls back to "All files" at once.
    const [selection, setSelection] = useState<{ orgId: string; docId: string } | null>(null);
    // Bumped after a delete so every org section re-keys and never shows the deleted file's figures.
    const [refreshEpoch, setRefreshEpoch] = useState(0);
    const { files, refetch: refetchFiles, dropFile } = useProvisionalFiles({ orgId, enabled: !isPDFReport && !isMobile });

    // Held with its org so a previous org's count never shows; a late response is dropped by the effect cleanup.
    const [heldNotIngested, setHeldNotIngested] = useState({ orgId: '', count: 0 });
    const [notIngestedRefreshKey, setNotIngestedRefreshKey] = useState(0);
    const notIngestedEnabled = !isPDFReport && !isMobile;
    const notIngestedCount = heldNotIngested.orgId === orgId ? heldNotIngested.count : 0;
    useEffect(() => {
        if (!notIngestedEnabled || !orgId) return undefined;
        let cancelled = false;
        fetchProvisionalNotIngestedCount(orgId).then((count) => {
            if (!cancelled) setHeldNotIngested({ orgId, count });
        });
        return () => {
            cancelled = true;
        };
    }, [notIngestedEnabled, orgId, notIngestedRefreshKey]);

    // The Magic Upload drawer can add files outside this page, so its close re-reads the count (as the Banking tab does).
    const isMagicUploadOpen = searchParams.get('magicUpload') === 'true';
    const wasMagicUploadOpen = useRef(false);
    useEffect(() => {
        if (wasMagicUploadOpen.current && !isMagicUploadOpen) setNotIngestedRefreshKey((key) => key + 1);
        wasMagicUploadOpen.current = isMagicUploadOpen;
    }, [isMagicUploadOpen]);

    // Review Now: Data Room filtered to Provisional Financials, folders with a Failed file open.
    const handleReviewNotIngestedClick = () => {
        const query = new URLSearchParams({
            tab: 'dataRoom',
            fileViewType: 'active',
            [DATA_ROOM_URL_PARAMS.DOC_TYPES]: PROVISIONAL_FINANCIALS_DOC_TYPE_ID,
            [DATA_ROOM_URL_PARAMS.EXPAND_FAILED]: '1',
        });
        navigate(`/investor/app/data-room/${orgId}?${query.toString()}`);
    };

    const selectedFile = selection?.orgId === orgId ? files.find((f) => f.docId === selection.docId) : undefined;
    const fileViewable = selectedFile?.viewStatus === PROVISIONAL_FILE_VIEW_STATUS.VIEWABLE;
    const selectFile = (docId: string | null) => setSelection(docId ? { orgId, docId } : null);

    // The select is hidden on mobile, so a kept selection would show file figures with no visible select.
    useEffect(() => {
        if (isMobile) setSelection(null);
    }, [isMobile]);

    const balanceSheet = useProvisionalStatementData({
        orgId,
        metric: resolvedMetrics.balanceSheet,
        nature: requestNature,
        refreshKey: refreshEpoch,
    });
    const profitAndLoss = useProvisionalStatementData({
        orgId,
        metric: resolvedMetrics.profitAndLoss,
        nature: requestNature,
        refreshKey: refreshEpoch,
    });
    const cashFlow = useProvisionalStatementData({
        orgId,
        metric: resolvedMetrics.cashFlow,
        nature: requestNature,
        refreshKey: refreshEpoch,
    });
    // Held back until the options (and with them the FY pair) arrive, so its first request already carries the nature.
    const cashFlowIndirect = useProvisionalStatementData({
        orgId,
        metric: optionsAreCurrent ? CASH_FLOW_INDIRECT_MIS : '',
        nature: requestNature,
        refreshKey: refreshEpoch,
    });
    // Other MIS has always loaded through the raw-classification path ('1' / Unclassified); it is not nature-split.
    const otherMis = useProvisionalStatementData({
        orgId,
        metric: resolvedMetrics.otherMis,
        raw: true,
        classificationId: '1',
        refreshKey: refreshEpoch,
    });

    // Balance Sheet and P&L only take a docId; waiting for the options makes the first request carry the nature.
    const balanceSheetFile = useProvisionalFileStatementData({
        orgId,
        docId: selectedFile?.docId,
        mis: PROVISIONAL_FILE_VIEW_MIS.balanceSheet,
        enabled: fileViewable && optionsAreCurrent,
        nature: requestNature,
    });
    const profitAndLossFile = useProvisionalFileStatementData({
        orgId,
        docId: selectedFile?.docId,
        mis: PROVISIONAL_FILE_VIEW_MIS.profitAndLoss,
        enabled: fileViewable && optionsAreCurrent,
        nature: requestNature,
    });

    // Only when the toggle's nature differs from the file's echo; an empty view's echo is the STANDALONE fallback.
    const displayedNature = toNature(auditedType);
    const echoedNature = balanceSheetFile.tableData.provisionalNature ?? profitAndLossFile.tableData.provisionalNature;
    const fileHasRows = (balanceSheetFile.tableData.rows?.length ?? 0) + (profitAndLossFile.tableData.rows?.length ?? 0) > 0;
    const fileNatureNote =
        fileViewable && displayedNature && fileHasRows && echoedNature && echoedNature !== displayedNature
            ? PROVISIONAL_FILE_NATURE_NOTE(natureToAuditedType(echoedNature as FinancialNature))
            : null;

    const fileDelete = useProvisionalFileDelete({
        orgId,
        onDeleted: (docId) => {
            setSelection(null);
            dropFile(docId);
            refetchFiles();
            fetchOptions();
            setRefreshEpoch((epoch) => epoch + 1);
        },
    });
    const canDeleteFile = !!selectedFile && !isLender && !isPartner && !isMobile && !isPDFReport;

    // RECUR-28096 — while an uploaded provisional file is still being extracted, show a loader in
    // place of "(No data available)" instead of leaving the tab looking empty/broken. No poll in
    // PDF-report mode (A-22).
    const { inProgress } = useFinancialsIngestionStatus({
        orgId,
        fileType: FINANCIALS_FILE_TYPE.PROVISIONAL,
        enabled: !isPDFReport,
        onCompleted: () => {
            // MUST be first: an org with no prior provisional data has every `resolvedMetrics.*`
            // stuck at '' (metricOptions is []), so each refetch() below would early-return at
            // useProvisionalStatementData.ts:48 with nothing to fetch. Refetching options flips
            // metric to a real value, which fires that hook's own effect and fetches the statement.
            fetchOptions();
            [balanceSheet, profitAndLoss, cashFlow, cashFlowIndirect, otherMis].forEach((section) => section.refetch());
            // A selected file that just turned viewable re-derives `enabled` and fetches on its own.
            refetchFiles();
            balanceSheetFile.refetch();
            profitAndLossFile.refetch();
        },
    });

    // A new upload flips inProgress false->true within one poll; list it now rather than when extraction completes.
    const prevInProgressRef = useRef(inProgress);
    useEffect(() => {
        if (!prevInProgressRef.current && inProgress) refetchFiles();
        prevInProgressRef.current = inProgress;
    }, [inProgress, refetchFiles]);

    // Each fixed layout is extended with any classification the payload returned but the layout does
    // not name, so an unverified layout can never silently drop a figure.
    const statutorySections: ProvisionalStatutorySection[] = useMemo(() => {
        const build = (key: ProvisionalStatementKey, layout: AuditedRowSpec[], raw: any) => {
            const data = filterParentHeader(raw);
            return { key, layout: appendUnmatchedRows(layout, data), data };
        };
        return [
            // A non-viewable file replaces Balance Sheet and P&L with a status note, so they are left out.
            ...(selectedFile && !fileViewable
                ? []
                : [
                      build('balanceSheet', BALANCE_SHEET_LAYOUT, fileViewable ? balanceSheetFile.tableData : balanceSheet.tableData),
                      build('profitAndLoss', PROFIT_AND_LOSS_LAYOUT, fileViewable ? profitAndLossFile.tableData : profitAndLoss.tableData),
                  ]),
            // Cash Flow and Cashflow Statement (Indirect Method) exist only at org level, so file mode omits them.
            ...(selectedFile
                ? []
                : [
                      build('cashFlow', CASH_FLOW_LAYOUT, cashFlow.tableData),
                      build('cashFlowIndirect', CASH_FLOW_INDIRECT_LAYOUT, keepComputablePeriods(cashFlowIndirect.tableData)),
                  ]),
        ];
    }, [
        selectedFile,
        fileViewable,
        balanceSheet.tableData,
        profitAndLoss.tableData,
        balanceSheetFile.tableData,
        profitAndLossFile.tableData,
        cashFlow.tableData,
        cashFlowIndirect.tableData,
    ]);

    // The org-wide poll reports some file extracting, not the selected one, so file mode drops it for Balance Sheet and P&L.
    const sectionInProgress = (key: ProvisionalStatementKey) =>
        selectedFile && (key === 'balanceSheet' || key === 'profitAndLoss') ? false : inProgress;

    const otherMisData = useMemo(() => filterParentHeader(otherMis.tableData), [otherMis.tableData]);

    // One page-level spinner covers the initial fetch, matching the audited page — not one loader per
    // statement, so the sections appear together rather than popping in one at a time.
    // Gated on what the current mode renders, so a pending org-only fetch cannot hold the spinner over a file's sections.
    const statementsLoading = selectedFile
        ? balanceSheetFile.loading || profitAndLossFile.loading
        : balanceSheet.loading || profitAndLoss.loading || cashFlow.loading || cashFlowIndirect.loading;
    const isLoading = optionsLoading || otherMis.loading || statementsLoading;

    // Measured on the band, applied to both rows: the band is full-bleed and the controls row sits
    // inside the content padding, which makes their content boxes the same width. What narrows them
    // is the Comments drawer, not the viewport, so this tracks the header rather than a media query.
    // The PDF page is a fixed 718px that must always render the full inline layout, as Financial
    // Summary also gates it — otherwise the layout depends on where 718 falls among the thresholds.
    const [headerRef, headerWidth] = useElementWidth();
    const responsive = !isPDFReport && headerWidth > 0;
    const collapseWidth = isLender ? HEADER_COLLAPSE_ACTIONS_WIDTH_LENDER : HEADER_COLLAPSE_ACTIONS_WIDTH_INVESTOR;
    const collapseActions = isMobile || (responsive && headerWidth < collapseWidth);
    const hideTitle = responsive && headerWidth < HEADER_HIDE_TITLE_WIDTH;
    const hideFileLabel = responsive && headerWidth < HEADER_HIDE_FILE_LABEL_WIDTH;

    const showFinancialMetrics = () => {
        updateQueryParams(searchParams, setSearchParams, { showFinancialMetrics: true }, location);
    };

    return {
        orgId,
        isLender,
        isMobile,
        headerRef,
        collapseActions,
        hideTitle,
        hideFileLabel,
        files,
        selectedFile,
        selectFile,
        fileViewable,
        fileNatureNote,
        canDeleteFile,
        fileDelete,
        sectionInProgress,
        notIngestedCount,
        handleReviewNotIngestedClick,
        unit,
        setUnit,
        resolvedMetrics,
        auditedType,
        auditedTypeOptionsObject,
        handleConsolidationChange,
        statutorySections,
        otherMisData,
        rawOptions,
        dataFormatOptions,
        isLoading,
        showFinancialMetrics,
        inProgress,
        viewer,
    };
}
